import { msg } from '@lingui/core/macro';
import type { CompletionPeriod, StatisticsType } from '@routes/sidebar/___shared___/api/statistics.api.ts';
import { STATISTIC_DATERANGES, STATISTICS_TYPES } from '@routes/sidebar/___shared___/constants.ts';
import { getPercentage } from '@shared/utils/numbers';
import { translateItem } from '@utils/translations.ts';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

export const STATISTICS_CHANGE_TYPE = {
    INCREASE: 'increase',
    DECREASE: 'decrease',
    NEUTRAL: 'neutral',
};

export type CHANGE_TYPE = (typeof STATISTICS_CHANGE_TYPE)[keyof typeof STATISTICS_CHANGE_TYPE];
export const TAB_TYPES = [STATISTICS_TYPES.Minutes, STATISTICS_TYPES.Elements, STATISTICS_TYPES.Pathways];
// Short month names for the chart axis. Context keeps them apart from other texts like "May".
export const MONTHS = [
    msg({ message: 'Jan', context: 'month' }),
    msg({ message: 'Feb', context: 'month' }),
    msg({ message: 'Mar', context: 'month' }),
    msg({ message: 'Apr', context: 'month' }),
    msg({ message: 'May', context: 'month' }),
    msg({ message: 'Jun', context: 'month' }),
    msg({ message: 'Jul', context: 'month' }),
    msg({ message: 'Aug', context: 'month' }),
    msg({ message: 'Sep', context: 'month' }),
    msg({ message: 'Oct', context: 'month' }),
    msg({ message: 'Nov', context: 'month' }),
    msg({ message: 'Dec', context: 'month' }),
];
export const NUMBER_OF_MONTHS = 6;

interface Statistic {
    type: StatisticsType;
    total: number;
    totalCompleted: number;
    monthlyCompleted: any[];
    weekCompleted: number;
}
export const getCurrentStatTextAndChart = (
    selectedTabSection: number,
    calculatedStatistics: Statistic[],
    completedTextTranslated: string,
) => {
    const tabType = TAB_TYPES[selectedTabSection];
    const isMinutesTab = TAB_TYPES[selectedTabSection] === STATISTICS_TYPES.Minutes;
    const tabStats = calculatedStatistics.find((s) => s.type === tabType)!;
    const monthlyCompletedRaw = tabStats.monthlyCompleted;
    const monthCompletedRaw =
        monthlyCompletedRaw.length === 0 ? 0 : monthlyCompletedRaw[monthlyCompletedRaw.length - 1];

    const monthlyCompleted = isMinutesTab ? monthlyCompletedRaw.map((x) => minutesToHours(x)) : monthlyCompletedRaw;
    const weekCompletedText = isMinutesTab
        ? formatHours(minutesToHours(tabStats.weekCompleted))
        : tabStats.weekCompleted;
    const monthCompletedText = isMinutesTab ? formatHours(minutesToHours(monthCompletedRaw)) : monthCompletedRaw;
    const completedVsTotalText = isMinutesTab
        ? `${formatHours(minutesToHours(tabStats.totalCompleted))} / ${formatHours(minutesToHours(tabStats.total))}`
        : `${tabStats.totalCompleted} / ${tabStats.total}`;
    const completedText = isMinutesTab ? completedTextTranslated : `${tabStats.type}   ${completedTextTranslated}`;
    const progressPercent = getPercentage(tabStats.total, tabStats.totalCompleted);

    return {
        monthlyCompleted,
        weekCompletedText,
        monthCompletedText,
        completedVsTotalText,
        completedText,
        progressPercent,
    };
};

export const formatHours = (hours: number): string => translateItem(msg`${hours} h`);

export const minutesToHours = (minutes: number): number => {
    return Number((minutes / 60).toFixed(1));
};

export const thisWeekCompleted = (completionPeriods: CompletionPeriod[], referenceDate: Date) => {
    const weekly = completionPeriods.find((cp) => cp.statisticDateRange === STATISTIC_DATERANGES.Week);
    if (weekly && weekly.values.length > 0) {
        const weekInMonth = (d: string | Date) => dayjs(d).startOf('isoWeek').format('YYYY-MM-DD');
        const periodWeek = weekInMonth(weekly.values[0].dateTimeUTCPeriod);
        const thisWeek = weekInMonth(referenceDate);

        if (periodWeek === thisWeek) {
            return weekly.values[0].totalCompleted;
        }
    }
    return 0;
};

export const periodsToDateRange = (completionPeriods: CompletionPeriod[], referenceDate: Date) => {
    const monthly = completionPeriods.find((cp) => cp.statisticDateRange === STATISTIC_DATERANGES.Month);
    if (monthly) {
        const keyCreator = (d: Date) => dayjs(d).format('YYYY-MM');
        const valueMap = new Map(
            monthly.values.map((item) => {
                const date = new Date(item.dateTimeUTCPeriod);
                const key = keyCreator(date);
                return [key, item.totalCompleted];
            }),
        );

        const result = [];

        for (let i = 0; i < NUMBER_OF_MONTHS; i++) {
            const date = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
            const key = keyCreator(date);
            result.push(valueMap.get(key) || 0);
        }

        return result.reverse();
    }
    return new Array(NUMBER_OF_MONTHS).fill(0);
};

export const calculateLastNMonths = (currentMonth: number, numberOfMonths: number): string[] => {
    const months: string[] = [];
    for (let i = 1; i <= numberOfMonths; i++) {
        const idx = (currentMonth - numberOfMonths + i + 12) % 12;
        months.push(translateItem(MONTHS[idx]));
    }
    return months;
};
